import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { AssemblyWithChildren, sortQuoteRequestLines } from "shared";

export type QuoteRequestLineNameSortDirection = "asc" | "desc";
export type QuoteRequestLineSortValue = "name-asc" | "name-desc" | "custom";

const compareNames = (a: string, b: string, direction: QuoteRequestLineNameSortDirection) =>
  direction === "asc"
    ? a.localeCompare(b, "fr", { sensitivity: "base" })
    : b.localeCompare(a, "fr", { sensitivity: "base" });

const isSequenceSortedByName = (
  names: string[],
  direction: QuoteRequestLineNameSortDirection,
): boolean => {
  for (let index = 1; index < names.length; index++) {
    const previousName = names[index - 1];
    const currentName = names[index];
    if (
      previousName !== undefined &&
      currentName !== undefined &&
      compareNames(previousName, currentName, direction) > 0
    ) {
      return false;
    }
  }
  return true;
};

const getMainAssemblies = (
  quoteRequestLines: QuoteRequestLineDto[],
): AssemblyWithChildren<QuoteRequestLineDto>[] =>
  Array.from(sortQuoteRequestLines(quoteRequestLines).batches.values())
    .map((assembliesInBatch) => assembliesInBatch[0])
    .filter((assembly): assembly is AssemblyWithChildren<QuoteRequestLineDto> => !!assembly);

const isAssemblySubtreeSortedByName = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  direction: QuoteRequestLineNameSortDirection,
): boolean => {
  const childNames = assembly.children.map((child) => child.quoteRequestLine.part.name);
  if (!isSequenceSortedByName(childNames, direction)) {
    return false;
  }
  return assembly.children.every((child) => isAssemblySubtreeSortedByName(child, direction));
};

const isQuoteRequestLineOrderSortedByName = (
  quoteRequestLines: QuoteRequestLineDto[],
  direction: QuoteRequestLineNameSortDirection,
): boolean => {
  const mainAssemblies = getMainAssemblies(quoteRequestLines);
  const rootNames = mainAssemblies.map((assembly) => assembly.quoteRequestLine.part.name);
  if (!isSequenceSortedByName(rootNames, direction)) {
    return false;
  }
  return mainAssemblies.every((assembly) => isAssemblySubtreeSortedByName(assembly, direction));
};

export const detectQuoteRequestLineSort = (
  quoteRequestLines: QuoteRequestLineDto[],
): QuoteRequestLineSortValue => {
  if (isQuoteRequestLineOrderSortedByName(quoteRequestLines, "asc")) {
    return "name-asc";
  }
  if (isQuoteRequestLineOrderSortedByName(quoteRequestLines, "desc")) {
    return "name-desc";
  }
  return "custom";
};

const flattenChildIds = (assembly: AssemblyWithChildren<QuoteRequestLineDto>): string[] =>
  assembly.children.flatMap((child) => [child.quoteRequestLine.id, ...flattenChildIds(child)]);

export const computeQuoteRequestLineNameSortOrderedIds = (
  quoteRequestLines: QuoteRequestLineDto[],
  direction: QuoteRequestLineNameSortDirection,
): string[] => {
  const { batches } = sortQuoteRequestLines(quoteRequestLines, {
    property: "name",
    order: direction,
  });

  return Array.from(batches.values()).flatMap((assembliesInBatch) => {
    const [mainAssembly, ...siblingAssemblies] = assembliesInBatch;
    if (!mainAssembly) {
      return [];
    }
    return [
      mainAssembly.quoteRequestLine.id,
      ...siblingAssemblies.map((sibling) => sibling.quoteRequestLine.id),
      ...flattenChildIds(mainAssembly),
    ];
  });
};
